// src/components/sections/ClientLogosWall.tsx
//
// "Wall of trust" — affichage créatif des logos clients.
// - 12 logos chargés depuis le bucket Supabase (Logos clients/1.png à 6 (1).png)
// - Asymétrie volontaire : grille à pas régulier mais avec rotation légère
//   par cellule pour casser l'effet "diaporama d'agence"
// - Stagger animation à l'apparition + hover lift + grayscale → couleur
// - Backdrop subtilement gradué (pas de bg blanc plat)
// - Préserve l'élément social proof "+50 entreprises"

import { motion } from 'framer-motion';
import Section from '../ui/section';
import SectionBackground from './section-background';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Liste des logos (12 fichiers vérifiés dans le bucket) —
const BUCKET_BASE =
  'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/images/Logos%20clients';

const logos: Array<{ file: string; alt: string; rotate: number }> = [
  { file: '1.png', alt: 'Client recruteurs.io', rotate: -1.5 },
  { file: '1 (1).png', alt: 'Client recruteurs.io', rotate: 0.8 },
  { file: '2.png', alt: 'Client recruteurs.io · Haschill', rotate: 1.2 },
  { file: '2 (1).png', alt: 'Client recruteurs.io', rotate: -0.6 },
  { file: '3.png', alt: 'Client recruteurs.io · Neodis', rotate: -1.8 },
  { file: '3 (1).png', alt: 'Client recruteurs.io', rotate: 1.5 },
  { file: '4.png', alt: 'Client recruteurs.io · Jarvis Group', rotate: 0.4 },
  { file: '4 (1).png', alt: 'Client recruteurs.io', rotate: -1.2 },
  { file: '5.png', alt: 'Client recruteurs.io', rotate: 1.8 },
  { file: '5 (1).png', alt: 'Client recruteurs.io', rotate: -0.5 },
  { file: '6.png', alt: 'Client recruteurs.io', rotate: 0.9 },
  { file: '6 (1).png', alt: 'Client recruteurs.io', rotate: -1.4 },
];

export interface ClientLogosWallProps {
  /** Identifiant section pour SectionBackground (ex: "recrutement-trust") */
  page: string;
  section?: string;
  /** Variante d'affichage :
   *  - "wall" (défaut) : grille 4 colonnes, hauteur généreuse, pour pages "trust" en bas de page
   *  - "strip" : marquee horizontal compact, idéal juste sous le hero pour réengager
   */
  variant?: 'wall' | 'strip';
  /** Eyebrow text (défaut : "Ils nous font confiance") */
  eyebrow?: string;
  /** Texte avant le span en gradient */
  titleStart?: string;
  /** Texte en gradient (fin du h2) */
  titleEnd?: string;
  /** Sous-titre optionnel */
  subtitle?: string;
  /** Footer text (défaut : ligne italique +50 entreprises) */
  footer?: string;
  /** className passée à <SectionBackground> */
  className?: string;
}

export default function ClientLogosWall({
  page,
  section = 'logos-wall',
  variant = 'wall',
  eyebrow = 'Ils nous font confiance',
  titleStart = 'Nous avons structuré ',
  titleEnd = 'leurs recrutements.',
  subtitle,
  footer = 'Plus de 50 entreprises accompagnées en toute confidentialité — cadres, dirigeants, postes pivots.',
  className = 'bg-white',
}: ClientLogosWallProps) {
  // — VARIANT "STRIP" : marquee compact infinite, idéal sous hero —
  if (variant === 'strip') {
    // On duplique les logos pour permettre une animation infinie sans saut visuel
    const looped = [...logos, ...logos];

    return (
      <SectionBackground page={page} section={section} className={className}>
        <div className="container-custom py-10 md:py-12">
          <div className="text-center mb-7">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-[1.15]">
              {titleStart}
              <span className={gradientText}>{titleEnd}</span>
            </h2>
          </div>

          {/* Marquee infini avec fade gauche/droite */}
          <div
            className="relative overflow-hidden"
            style={{
              maskImage:
                'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
            }}
          >
            <motion.div
              className="flex items-center gap-10 md:gap-14"
              animate={{ x: ['0%', '-50%'] }}
              transition={{
                duration: 45,
                ease: 'linear',
                repeat: Infinity,
              }}
              style={{ width: 'max-content' }}
            >
              {looped.map((logo, i) => (
                <img
                  key={`${logo.file}-${i}`}
                  src={`${BUCKET_BASE}/${encodeURIComponent(logo.file)}`}
                  alt={logo.alt}
                  loading="lazy"
                  className="h-10 md:h-12 w-auto flex-shrink-0 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300 mix-blend-multiply"
                />
              ))}
            </motion.div>
          </div>
        </div>
      </SectionBackground>
    );
  }

  // — VARIANT "WALL" (défaut) : grille créative —
  return (
    <SectionBackground page={page} section={section} className={className}>
      <Section>
        {/* — Header — */}
        <div className="max-w-4xl mx-auto text-center mb-14">
          <span className={eyebrowClass}>{eyebrow}</span>
          <h2 className={h2Class}>
            {titleStart}
            <span className={gradientText}>{titleEnd}</span>
          </h2>
          {subtitle && <p className={sectionSubtitleClass}>{subtitle}</p>}
        </div>

        {/* — Wall of logos — */}
        <div className="relative">
          {/* Halo décoratif derrière la grille (gradient brand très diffus) */}
          <div
            aria-hidden
            className="absolute inset-0 -z-10 pointer-events-none flex items-center justify-center"
          >
            <div className="w-3/4 h-3/4 max-w-3xl bg-gradient-to-br from-[#fde7f2]/40 via-transparent to-[#fde2d3]/40 rounded-full blur-3xl" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 max-w-5xl mx-auto">
            {logos.map((logo, index) => (
              <motion.div
                key={logo.file}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: index * 0.04 }}
                whileHover={{ y: -3, rotate: 0 }}
                style={{ rotate: `${logo.rotate}deg` }}
                className="group flex items-center justify-center h-24 md:h-28 rounded-2xl bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/30 hover:shadow-lg shadow-sm p-5 transition-all duration-300"
              >
                <img
                  src={`${BUCKET_BASE}/${encodeURIComponent(logo.file)}`}
                  alt={logo.alt}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300 mix-blend-multiply"
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* — Footer (social proof preserved) — */}
        {footer && (
          <p className="mt-12 text-sm text-neutral-400 italic text-center max-w-2xl mx-auto">
            {footer}
          </p>
        )}
      </Section>
    </SectionBackground>
  );
}
